/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import React from "react";

import { IconButton, useMediaQuery } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { useTheme } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";
import { FormContainerControlled, XIcon } from "components";
import { Permissions } from "configs";
import { message } from "contexts";
import { DialogFieldName, useDialog } from "stores/Dialog";
import { i18n } from "translations";
import { rem } from "utils";

import CommonButton from "./CommonButton";
import HookForm from "./HookForm";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface DialogFormProps {
  name: DialogFieldName;
  open?: boolean;
  onToggle?: () => void;
  title?: string;
  titleI18n?: string;
  formProps: Omit<
    React.ComponentProps<typeof FormContainerControlled>,
    "methods" | "children" | "onSubmit" | "onError"
  > & {
    methods: any;
    onSubmit?: (e?: any) => void;
    onError?: (e?: any) => void;
  };
  width?: number;
  dialogProps?: Partial<React.ComponentProps<typeof Dialog>>;
  hookFormProps?: React.ComponentProps<typeof HookForm>;
  action?: React.ReactNode;
  actionProps?: {
    saveDisabled?: boolean;
    saveLoading?: boolean;
    cancelDisabled?: boolean;
    permission?: keyof typeof Permissions;
  };
  saveBtnProps?: Partial<React.ComponentProps<typeof CommonButton>>;
  children?: React.ReactNode;
  showMessageRequired?: boolean;
}

const DialogForm: React.FC<DialogFormProps> = ({
  name,
  title,
  titleI18n,
  formProps,
  dialogProps,
  width,
  hookFormProps,
  actionProps,
  action,
  children,
  saveBtnProps,
  showMessageRequired,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(820));
  const {
    dialog: { show },
    onToggle,
  } = useDialog({ name });

  const handleClose = (_event: any, reason: string) => {
    if (reason && reason === "backdropClick") return;
    onToggle({});
  };

  const handleErrorForm = (e: any) => {
    if (showMessageRequired) {
      message({
        title: i18n.t("pleaseFillInRequiredFields"),
        type: "Error",
      });
    }
    return console.log(e);
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      scroll="paper"
      open={show}
      onClose={handleClose}
      disableEscapeKeyDown
      sx={{
        ...(width && {
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: `${width}px`,
            },
          },
        }),
      }}
      {...dialogProps}
    >
      <FormContainerControlled
        onSubmit={formProps?.onSubmit ? formProps?.onSubmit : console.log}
        onError={formProps?.onError ? formProps?.onError : handleErrorForm}
        {...formProps}
      >
        <DialogTitle
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            zIndex: 1,
            backgroundColor: "white",
          }}
        >
          {titleI18n ? i18n.t(titleI18n) : title}
          <IconButton
            aria-label="close"
            disabled={actionProps?.cancelDisabled || false}
            onClick={onToggle}
            sx={{
              position: "absolute",
              right: 15,
              top: 15,
              zIndex: 999,
            }}
          >
            <XIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {hookFormProps && <HookForm {...hookFormProps} />}
          {children}
        </DialogContent>
        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            width: "100%",
            background: "#FFFFFF",
            padding: `${rem(16)} ${rem(24)}`,
            borderRadius: "12px",
          }}
        >
          {action || (
            <>
              <CommonButton
                name="cancel"
                onClick={onToggle}
                disabled={actionProps?.cancelDisabled || false}
              />
              <CommonButton
                name="save"
                disabled={actionProps?.saveDisabled || false}
                loading={actionProps?.saveLoading || false}
                permission={actionProps?.permission}
                {...saveBtnProps}
              />
            </>
          )}
        </DialogActions>
      </FormContainerControlled>
    </Dialog>
  );
};

export default DialogForm;
